/**
 * Shapes and small helpers for the structured bot logs.
 *
 * The backend folds both log streams into the CURRENT STATE of every employee and
 * every file: an employee that failed once and succeeded on a later run arrives as
 * one success that remembers it took two attempts. `resolved` marks exactly those,
 * so the viewer can say "this was fixed" instead of showing a failure that is over.
 */

import { rosterRowLabel } from "./rosterFile";

// "employees" is the All logs tab: one row per employee with its file
// counts, plus the files matched to nobody (CAL-6160). The files of one
// employee are fetched on open with stream="file" and that row's owner_key.
export type LogStream = "all" | "status" | "file" | "employees";
export type LogState =
  | "all"
  | "error"
  | "success"
  | "warning"
  | "unknown"
  | "resolved";

export interface LogRecordDto {
  stream: "status" | "file";
  level: string;
  state: "success" | "error" | "warning" | "unknown";
  timestamp: string;
  raw: string;
  source_task_id: number | null;
  employee_name: string | null;
  employee_id: string | null;
  /** The employee's row in the roster file now (rosterFile.ts LOG_ROW_HINT). */
  inner_number: number | null;
  /** False when the roster no longer holds the employee: inner_number is then the row they had. */
  in_roster?: boolean | null;
  /** The Data Storage folder (Matches file s3_name) the record is about, when the roster confirmed it. */
  s3_name?: string | null;
  employee_status: string | null;
  company: string | null;
  total: number | null;
  downloaded: number | null;
  uploaded_to: string | null;
  uploaded_count: number | null;
  filename: string | null;
  reason: string | null;
  /** What a file-log line said when it names no file (some bots write notes). */
  message: string | null;
  attempts: number;
  resolved: boolean;
  /** Written by a task that has since been deleted from the job. */
  from_deleted_task: boolean;
  first_failed_at: string | null;
  last_failed_at: string | null;
  owner_task_id: number | null;
  /** Written by the "Manually resolved" action, not by a bot run. */
  manual?: boolean;
  /** Who marked the file or employee as uploaded by hand, and when (ISO, UTC). */
  resolved_by?: string | null;
  resolved_at?: string | null;
  /** The error screenshot a failing employee's record names, under error_screenshots/. */
  screenshot?: string | null;
  /** A failing employee's files that still fail (the latest few), and how many in all. */
  failed_files?: LogFailedFile[];
  failed_files_total?: number;
  /** Who the roster has on the row of a status record that names nobody. */
  roster_name?: string | null;
  /** Every file of this employee, saved and failed (CAL-6160). The files
   *  themselves are fetched when the row is opened - a job can hold hundreds
   *  of thousands of them. */
  files_saved_total?: number;
  files_failed_total?: number;
  /** The employee a record belongs to; null on a file matched to nobody. */
  owner_key?: string | null;
}

export interface LogFailedFile {
  filename: string | null;
  reason: string | null;
  state: string;
}

/** GET /tasks/jobs/<id>/error-screenshot/?name= - a link that expires. */
export interface ErrorScreenshotLink {
  url: string;
  expires_in: number;
  name: string;
}

export interface LogStateCounts {
  total: number;
  success: number;
  error: number;
  warning: number;
  unknown: number;
  resolved: number;
}

export interface LogRecordCounts extends LogStateCounts {
  status: LogStateCounts;
  file: LogStateCounts;
  unparsed: number;
  /** Job scope only: records whose employee number no task covers any more. */
  outside_ranges?: number;
  /** Job scope only: records that could not be tied to an employee at all. */
  unattributed?: number;
  /** Job scope only: records of employees the roster no longer holds. */
  not_in_roster?: number;
  /** Records written by tasks the job no longer has - still its work. */
  from_deleted_tasks?: number;
}

export interface LogRecordsRange {
  mode: string | null;
  from: number | null;
  to: number | null;
  merged_from: number | null;
  merged_count: number | null;
  total_employees: number | null;
  notice: string;
  /** Bootstrap bounds or an empty roster: from/to name nobody. */
  ranges_pending?: boolean;
  roster_empty?: boolean;
}

export interface LogRecordsTask {
  id: number;
  from: number | null;
  to: number | null;
  task_status: string;
}

export interface LogRecordsResponse {
  scope: "job" | "task";
  job_id: number | null;
  task_id: number | null;
  counts: LogRecordCounts;
  records: LogRecordDto[];
  total_matched: number;
  offset: number;
  limit: number;
  has_more: boolean;
  source: string | null;
  generated_at: string;
  range: LogRecordsRange;
  tasks: LogRecordsTask[];
  /** Whether "Manually resolved" may be offered on this job's ERROR employees. */
  manual_resolution?: ManualResolutionAvailability;
}

/** An upload job is resolved file by file, a download job for the employee as a whole. */
export type ManualResolutionKind = "upload" | "download";

export interface ManualResolutionAvailability {
  available: boolean;
  /** ok | unsupported_job_kind | tasks_in_flight | storage_unavailable | no_job */
  code: string;
  reason: string;
  /** null when the job's errors are not resolved by hand (delete, SSN). */
  kind?: ManualResolutionKind | null;
}

/** How the viewer names one employee row to the manual-resolution endpoint. */
export interface ManualResolutionKey {
  employee_id: string | null;
  inner_number: number | null;
  s3_name: string | null;
}

export type ManualResolutionFileStatus =
  | "rejected"
  | "resolved_manually"
  | "not_attempted";

export interface ManualResolutionFile {
  filename: string;
  status: ManualResolutionFileStatus;
  /** "log": the file logs name it; "folder": listed from Data Storage instead. */
  source: "log" | "folder";
  state: string | null;
  level: string | null;
  reason: string | null;
  timestamp: string | null;
  source_task_id: number | null;
  resolved_by: string | null;
  resolved_at: string | null;
  attempts: number;
  size: number | null;
}

export interface ManualResolutionEmployee {
  employee_name: string | null;
  employee_id: string | null;
  inner_number: number | null;
  s3_name: string | null;
  state: string;
  resolved: boolean;
  manual: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  owner_task_id: number | null;
  source_task_id: number | null;
  total: number | null;
  downloaded: number | null;
  uploaded_to: string | null;
  uploaded_count: number | null;
  attempts: number;
  timestamp: string | null;
}

export interface ManualResolutionPreview {
  job_id: number;
  kind: ManualResolutionKind;
  mode: "zip" | "copy";
  manual_resolution: ManualResolutionAvailability;
  employee: ManualResolutionEmployee;
  files: ManualResolutionFile[];
  counts: Record<ManualResolutionFileStatus, number>;
  files_source: "log" | "folder";
  target_archive: { task_id: number; key: string } | null;
  can_resolve: boolean;
  blockers: string[];
}

export interface ManualResolutionSaveRequest extends ManualResolutionKey {
  resolve: string[];
  undo: string[];
  /** Only when the list of files is empty: resolve / un-resolve the employee as a whole. */
  resolve_employee?: boolean;
  undo_employee?: boolean;
}

export interface ManualResolutionSaveResponse {
  job_id: number;
  employee: ManualResolutionEmployee;
  resolved_files: string[];
  undone_files: string[];
  remaining_files: string[];
  employee_resolved: boolean;
  status_record: "append" | "remove" | "unchanged";
  records: LogRecordDto[];
  archives: Array<{
    task_id: number;
    key: string;
    etag: string;
    appended: number;
    removed: number;
  }>;
  counters_refresh: string;
}

export interface LogRecordsQuery {
  stream?: LogStream;
  state?: LogState;
  q?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  refresh?: boolean;
  /** One employee's files, by the owner_key its row carries (CAL-6160). An
   *  empty string is a real key, so only leaving it out means "everything". */
  employee?: string;
}

export const EMPTY_STATE_COUNTS: LogStateCounts = {
  total: 0,
  success: 0,
  error: 0,
  warning: 0,
  unknown: 0,
  resolved: 0,
};

export function buildLogRecordsQuery(params: LogRecordsQuery): string {
  const search = new URLSearchParams();
  if (params.stream && params.stream !== "all") search.append("stream", params.stream);
  if (params.state && params.state !== "all") search.append("state", params.state);
  if (params.q) search.append("q", params.q);
  if (params.order) search.append("order", params.order);
  if (params.limit != null) search.append("limit", String(params.limit));
  if (params.offset) search.append("offset", String(params.offset));
  if (params.refresh) search.append("refresh", "1");
  if (params.employee != null) search.append("employee", params.employee);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function manualResolutionKey(record: LogRecordDto): ManualResolutionKey {
  return {
    employee_id: record.employee_id ?? null,
    inner_number: record.inner_number ?? null,
    s3_name: record.s3_name ?? null,
  };
}

/** Stable identity of an employee row - unlike recordKey it carries no list index. */
export function manualResolutionIdentity(record: LogRecordDto): string {
  return [record.inner_number ?? "", record.employee_id ?? "", record.s3_name ?? ""].join(
    "|",
  );
}

export function buildManualResolutionQuery(key: ManualResolutionKey): string {
  const search = new URLSearchParams();
  if (key.employee_id) search.append("employee_id", key.employee_id);
  if (key.inner_number != null) search.append("inner_number", String(key.inner_number));
  if (key.s3_name) search.append("s3_name", key.s3_name);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/** The record's current state was written by a person, not by a bot run. */
export function isManuallyResolved(record: LogRecordDto): boolean {
  return Boolean(record.manual || record.resolved_by);
}

export function formatResolvedAt(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function resolvedBadgeLabel(record: LogRecordDto): string {
  return isManuallyResolved(record) ? "RESOLVED (manually)" : "RESOLVED";
}

export function resolvedBadgeTooltip(record: LogRecordDto): string {
  if (isManuallyResolved(record)) {
    const who = record.resolved_by || "an operator";
    const when = formatResolvedAt(record.resolved_at);
    const marked = `Marked as resolved by ${who}${when ? ` at ${when}` : ""} - uploaded by hand, not by the bot.`;
    return record.first_failed_at ? `Failed at ${record.first_failed_at}; ${marked}` : marked;
  }
  return record.first_failed_at
    ? `Failed at ${record.first_failed_at}, fixed by a later run`
    : "Failed earlier, fixed by a later run";
}

/** "Lee, Ann (row 12)" - the employee as the dialog, its outcome and the toast name them. */
export function employeeLabel(employee: {
  employee_name: string | null;
  employee_id: string | null;
  inner_number: number | null;
}): string {
  const name = employee.employee_name || employee.employee_id || "(no employee)";
  return employee.inner_number != null
    ? `${name} (${rosterRowLabel(employee.inner_number)})`
    : name;
}

/** Same palette as the raw log colorizer, so both views read alike. */
export const LOG_STATE_COLORS: Record<LogRecordDto["state"], string> = {
  success: "success.main",
  error: "error.main",
  warning: "warning.main",
  unknown: "text.disabled",
};

export const LOG_STATE_CHIP_COLOR: Record<
  LogRecordDto["state"],
  "success" | "error" | "warning" | "default"
> = {
  success: "success",
  error: "error",
  warning: "warning",
  unknown: "default",
};

/** Counts for the stream a tab is showing. */
export function countsForStream(
  counts: LogRecordCounts | undefined,
  stream: LogStream,
): LogStateCounts {
  if (!counts) return EMPTY_STATE_COUNTS;
  if (stream === "status") return counts.status ?? EMPTY_STATE_COUNTS;
  if (stream === "file") return counts.file ?? EMPTY_STATE_COUNTS;
  // The employees tab lists employees, so its chips count employees. Counting
  // both streams there read as "4 errors" over a list where no employee
  // failed - the four were documents (CAL-6160). The file numbers are not
  // lost: fileCountsOf puts them in a chip of their own.
  if (stream === "employees") return counts.status ?? EMPTY_STATE_COUNTS;
  return counts;
}

/** The file-stream counts, for the employees tab's "N documents failed" chip. */
export function fileCountsOf(
  counts: LogRecordCounts | undefined,
): LogStateCounts {
  return counts?.file ?? EMPTY_STATE_COUNTS;
}

/** "2026-08-21 12:48:17,315" -> "12:48:17" (the date sits in the tooltip). */
export function formatLogTime(timestamp: string): string {
  const time = timestamp.split(" ")[1] || timestamp;
  return time.split(",")[0] || time;
}

export function formatLogDate(timestamp: string): string {
  return timestamp.split(" ")[0] || "";
}

/** What the row is about: the employee for a status record, the file for a file one. */
export function recordTitle(record: LogRecordDto): string {
  if (record.stream === "file") {
    // Not every file-log line is about a file: some bots write plain notes to
    // that stream, and the note itself is what the row should say.
    return (
      record.filename ||
      record.message ||
      record.employee_name ||
      "(no file name)"
    );
  }
  return (
    record.employee_name || record.roster_name || record.employee_id || "(no employee)"
  );
}

/** The title is the roster's name for the row, not one the bot wrote. */
export function titleIsFromRoster(record: LogRecordDto): boolean {
  return record.stream === "status" && !record.employee_name && Boolean(record.roster_name);
}

export const ROSTER_NAME_HINT =
  "The bot failed before it read who this was; this is who the roster has on that row.";

/** Why a failing employee failed, in one line: the bot's reason, else its failing files. */
export function failureReasonLine(record: LogRecordDto): string | null {
  if (record.stream !== "status" || (record.state !== "error" && record.state !== "warning")) {
    return null;
  }
  if (record.reason) return record.reason;
  const total = record.failed_files_total ?? 0;
  const first = record.failed_files?.[0];
  if (!total || !first) return null;
  const head = `${total} ${total === 1 ? "file" : "files"} still failing`;
  const detail = [first.filename, first.reason].filter(Boolean).join(" - ");
  return detail ? `${head}: ${detail}` : head;
}

/** What the bot noted on an employee it completed - a document the portal would
 * not serve, a step left to someone else. The employee is not an error. */
export function recordNote(record: LogRecordDto): string | null {
  if (record.stream !== "status" || record.state !== "success") return null;
  return record.reason || null;
}

/** Row keys that survive a refresh and "Load more": what the record is, numbered
 * only among records that are otherwise the same - never by position in the list. */
export function recordKeys(records: LogRecordDto[]): string[] {
  const seen = new Map<string, number>();
  return records.map((record) => {
    const base = [
      record.stream,
      record.timestamp,
      record.source_task_id ?? "",
      record.employee_id ?? record.employee_name ?? "",
      record.filename ?? record.message ?? "",
      record.inner_number ?? "",
    ].join("|");
    const repeat = seen.get(base) ?? 0;
    seen.set(base, repeat + 1);
    return repeat ? `${base}#${repeat}` : base;
  });
}

/** The line under the title: what happened, in the record's own numbers. */
export function recordSummary(record: LogRecordDto): string {
  if (record.stream === "file") {
    const parts: string[] = [];
    if (record.employee_name) parts.push(record.employee_name);
    if (record.reason && record.reason !== record.message) parts.push(record.reason);
    return parts.join(" - ");
  }
  const parts: string[] = [];
  if (record.total != null) parts.push(`total ${record.total}`);
  if (record.downloaded != null) parts.push(`downloaded ${record.downloaded}`);
  if (record.uploaded_count != null) {
    parts.push(
      record.uploaded_to
        ? `uploaded to ${record.uploaded_to} ${record.uploaded_count}`
        : `uploaded ${record.uploaded_count}`,
    );
  }
  if (record.company) parts.push(record.company);
  return parts.join(" - ");
}
